import React, { useState, useEffect } from 'react';

import { showToast } from '@/src/globalFunction';

import {
  useGetProjectTags,
  useDeleteProjectTag,
  useUpdateProjectTag,
  useDetachInboxDocumentTag,
  invalidateAllFilesQueries,
} from './api/projectTags';
import AssignedTags from './Elements/AssignedTags';
import ConfirmTagActionModal from './Elements/ConfirmTagActionModal';
import TagEntry from './Tag';
import { TagType } from './types';
import { useHasFullTagsAccess } from './utils';

import { useInbox } from '../Inbox2/store/inbox.store';
import { useTagsStore } from '../Inbox2/store/tags.store';

interface InboxDocumentTagsManagerProps {
  rowId?: any;
  inboxDocumentId: any;
  projectId: any;
  refetchFiles?: () => void;
  assignedTags?: TagType[];
  setAssignedTags?: (tags: TagType[]) => void;
  fullAccessPermissions?: boolean;
  style?: React.CSSProperties;
}

const InboxDocumentTagsManager: React.FC<InboxDocumentTagsManagerProps> = ({
  rowId,
  inboxDocumentId,
  projectId,
  assignedTags,
  setAssignedTags,
  style,
}) => {
  const { setFileTags, updateFileTags } = useInbox();
  const { setTags, tags: globalProjectTags, permissions } = useTagsStore((state) => state);

  const { data: projectTags, refetch: refetchProjectTags } = useGetProjectTags(projectId, {
    enabled: true,
  });
  const deleteProjectTagMutation = useDeleteProjectTag(Number(projectId));
  const updateProjectTagMutation = useUpdateProjectTag(projectId);
  const detachInboxDocumentTagMutation = useDetachInboxDocumentTag(inboxDocumentId);

  // Sync global store with latest projectTags
  useEffect(() => {
    if (Array.isArray(projectTags)) {
      setTags(projectTags);
    }
  }, [projectTags, setTags]);

  // Always use assignedTags and setAssignedTags from props as source of truth
  const assignedTagsFinal = assignedTags || [];
  const setAssignedTagsFinal = setAssignedTags || (() => {});

  // Local UI state
  const [editState, setEditState] = useState<{
    openId: string | null;
    editTagIndex: number;
  }>({ openId: null, editTagIndex: -1 });
  const [showConfirmModal, setShowConfirmModal] = useState<null | number>(null);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState<null | number>(null);
  const [pendingUpdate, setPendingUpdate] = useState<{
    tagIndex: number;
    updatedTag: TagType;
  } | null>(null);

  // Permissions
  const hasFullTagsAccess = useHasFullTagsAccess(permissions?.inbox.actions);

  // Tag delete handler
  const handleRemoveTag = (tagIndex: number) => {
    setShowConfirmModal(tagIndex);
  };
  const handleConfirmDelete = () => {
    if (showConfirmModal === null) return;
    const tag = assignedTagsFinal[showConfirmModal];
    if (!tag) return;
    setShowConfirmModal(null);
    deleteProjectTagMutation.mutate(
      { tagId: Number(tag.id), version: tag.version },
      {
        onSuccess: () => {
          showToast('success', 'Tag deleted successfully');
          const newTags = assignedTagsFinal.filter((_, idx) => idx !== showConfirmModal);
          setAssignedTagsFinal(newTags);
          setTags(globalProjectTags.filter((t) => t.id !== tag.id));
          invalidateAllFilesQueries();
          refetchProjectTags();
          setEditState({ openId: null, editTagIndex: null });
        },
      }
    );
  };

  // Tag edit handlers
  const handleEditTag = (tagIndex: number) =>
    setEditState({ openId: rowId, editTagIndex: tagIndex });
  const handleEditTagSave = (tagIndex: number, updatedTag: TagType) => {
    setPendingUpdate({ tagIndex, updatedTag });
    setShowUpdateConfirmModal(tagIndex);
  };
  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      handleRequestUpdate(pendingUpdate.tagIndex, pendingUpdate.updatedTag);
    }
    setShowUpdateConfirmModal(null);
    setPendingUpdate(null);
  };

  // Tag unassign handler
  const handleUnassignTag = (tagIndex: number) => {
    const tag = assignedTagsFinal[tagIndex];
    if (!tag || !inboxDocumentId) return;
    detachInboxDocumentTagMutation.mutate(tag.id as number, {
      onSuccess: () => {
        const newTags = assignedTagsFinal.filter((_, idx) => idx !== tagIndex);
        setAssignedTagsFinal(newTags);
        updateFileTags(String(inboxDocumentId), newTags);
        setFileTags(String(inboxDocumentId), newTags);
        invalidateAllFilesQueries();
      },
    });
  };

  // Tag update handler
  const handleRequestUpdate = (tagIndex: number, updatedTag: TagType) => {
    if (!projectId) return;
    updateProjectTagMutation.mutate(
      {
        tagId: updatedTag.id,
        body: {
          name: updatedTag.name,
          color: updatedTag.color,
          version: updatedTag.version,
        },
      },
      {
        onSuccess: (updatedTagFromServer) => {
          const newTags = assignedTagsFinal.map((tag, idx) =>
            idx === tagIndex ? { ...tag, ...updatedTagFromServer } : tag
          );
          invalidateAllFilesQueries();
          showToast('success', 'Tag updated successfully');
          setAssignedTagsFinal(newTags);
          setEditState({ openId: null, editTagIndex: -1 });
          setTags(
            globalProjectTags.map((tag) =>
              tag.id === updatedTagFromServer.id ? { ...tag, ...updatedTagFromServer } : tag
            )
          );
        },
      }
    );
  };

  return (
    <>
      {assignedTagsFinal.length > 0 ? (
        <>
          <AssignedTags
            assignedTags={assignedTagsFinal}
            rowId={rowId}
            onRemoveTag={handleRemoveTag}
            onEdit={handleEditTag}
            onUnassignTag={handleUnassignTag}
            isTagReadOnly={!hasFullTagsAccess}
            style={style}
          />
          {editState.openId === rowId && editState.editTagIndex !== null && (
            <TagEntry
              id={rowId}
              openId={editState.openId}
              setOpenId={(id) => setEditState((prev) => ({ ...prev, openId: id }))}
              assignedTags={assignedTagsFinal}
              setAssignedTags={setAssignedTagsFinal}
              editTagIndex={editState.editTagIndex}
              setEditTagIndex={(idx) => setEditState((prev) => ({ ...prev, editTagIndex: idx }))}
              allProjectTags={globalProjectTags}
              inboxDocumentId={inboxDocumentId}
              onRequestUpdate={handleRequestUpdate}
              onSave={handleEditTagSave}
              isTagReadOnly={!hasFullTagsAccess}
            />
          )}
        </>
      ) : (
        <TagEntry
          id={rowId}
          openId={editState.openId}
          setOpenId={(id) => setEditState((prev) => ({ ...prev, openId: id }))}
          assignedTags={assignedTagsFinal}
          setAssignedTags={setAssignedTagsFinal}
          editTagIndex={editState.editTagIndex}
          setEditTagIndex={(idx) => setEditState((prev) => ({ ...prev, editTagIndex: idx }))}
          allProjectTags={globalProjectTags}
          inboxDocumentId={inboxDocumentId}
          onRequestUpdate={handleRequestUpdate}
          isTagReadOnly={!hasFullTagsAccess}
        />
      )}
      {showConfirmModal !== null && (
        <ConfirmTagActionModal
          onClose={() => setShowConfirmModal(null)}
          onSubmit={handleConfirmDelete}
          subject={'Tag'}
          actionType="delete"
        />
      )}
      {showUpdateConfirmModal !== null && (
        <ConfirmTagActionModal
          onClose={() => {
            setShowUpdateConfirmModal(null);
            setPendingUpdate(null);
          }}
          onSubmit={handleConfirmUpdate}
          subject={'Tag'}
          actionType="update"
        />
      )}
    </>
  );
};

export default InboxDocumentTagsManager;
